import type { StockingLineage, StockingLineageBlocker } from '@/domain/stockingCycles';

import type { BoardCycle } from '../types';

/** The choice value for "New stocking"; every other value is a stocking's id. */
export const NEW_STOCKING_CHOICE = 'new';

/**
 * What "Add to stocking" can offer a lineage: nothing (it leaves the site or the client, or is
 * spread over several stockings), only the stocking part of it is in already, or any of the
 * site's stockings and a new one.
 */
export type AssignOffer =
    | { kind: 'blocked'; blocker: StockingLineageBlocker }
    | { kind: 'severalStockings'; names: string[] }
    | { kind: 'joins'; cycle: BoardCycle }
    | { kind: 'choose'; cycles: BoardCycle[] };

function cycleNames(ids: readonly number[], cycles: readonly BoardCycle[]): string[] {
    return ids.map((id) => cycles.find((cycle) => cycle.id === id)?.name ?? String(id));
}

export function assignOffer(lineage: StockingLineage, cycles: BoardCycle[]): AssignOffer {
    if (lineage.blocker) return { kind: 'blocked', blocker: lineage.blocker };
    const [only, ...others] = lineage.stockingCycleIds;
    if (others.length > 0) {
        return { kind: 'severalStockings', names: cycleNames(lineage.stockingCycleIds, cycles) };
    }
    const joined = only === undefined ? undefined : cycles.find((cycle) => cycle.id === only);
    if (joined) return { kind: 'joins', cycle: joined };
    // A stocking the board doesn't list can't be picked; the backend would name it on refusal.
    if (only !== undefined) return { kind: 'severalStockings', names: cycleNames([only], cycles) };
    return { kind: 'choose', cycles };
}

/** The choice an offer starts on: the stocking it joins, the selectable first, else a new one. */
export function defaultAssignChoice(offer: AssignOffer): string {
    if (offer.kind === 'joins') return String(offer.cycle.id);
    if (offer.kind === 'choose') {
        const open = offer.cycles.find((cycle) => cycle.status !== 'completed');
        return open ? String(open.id) : NEW_STOCKING_CHOICE;
    }
    return NEW_STOCKING_CHOICE;
}

/** "Remove from stocking" is refused for the same lineages, or one also in another stocking. */
export type RemoveOffer =
    | { kind: 'blocked'; blocker: StockingLineageBlocker }
    | { kind: 'severalStockings'; names: string[] }
    | { kind: 'ok' };

export function removeOffer(
    lineage: StockingLineage,
    stockingCycleId: number,
    cycles: BoardCycle[]
): RemoveOffer {
    if (lineage.blocker) return { kind: 'blocked', blocker: lineage.blocker };
    if (lineage.stockingCycleIds.some((id) => id !== stockingCycleId)) {
        return { kind: 'severalStockings', names: cycleNames(lineage.stockingCycleIds, cycles) };
    }
    return { kind: 'ok' };
}

/**
 * The site's other ongoing stockings once the lineage is in `targetId` (null: a new stocking),
 * when its type runs one at a time: what the backend answers as `CONCURRENT_ACTIVE_CYCLE`. A
 * site manager's lineage leaves no other stocking, so each ongoing one stays ongoing; the
 * target is ongoing afterwards when it was already, or when the lineage has a live group.
 */
export function concurrentStockings(
    lineage: StockingLineage,
    cycles: BoardCycle[],
    targetId: number | null
): BoardCycle[] {
    if (!lineage.expectsSingleActiveCycle) return [];
    const target = cycles.find((cycle) => cycle.id === targetId);
    const targetOngoing = target?.status === 'active' || lineage.groups.some((g) => g.isLive);
    if (!targetOngoing) return [];
    return cycles.filter((cycle) => cycle.status === 'active' && cycle.id !== targetId);
}
